import { useEffect, useState } from "react";
import { NavLink, Routes, Route, useNavigate, Navigate } from "react-router-dom";
import { api, getUser } from "@/lib/api";
import { toast } from "sonner";
import {
  ChartLineUp, Users, Briefcase, CalendarBlank, ShieldCheck, ArrowRight,
  CheckCircle, XCircle, Money, CurrencyDollar, MapPin, WhatsappLogo,
} from "@phosphor-icons/react";
import { VERTICAL_MAP } from "@/lib/api";

// Build a wa.me URL with a pre-filled dispatcher message summarising the booking.
const BookingWhatsAppLink = ({ booking, testIdPrefix = "wa" }) => {
  const gift = booking.is_gift;
  const targetPhone = gift ? (booking.gift_recipient_phone || booking.customer_phone) : (booking.customer_phone);
  const targetName = gift ? (booking.gift_recipient_name || "the recipient") : (booking.customer_name || "the customer");
  const digits = String(targetPhone || "").replace(/[^\d]/g, "");
  if (!digits) return null;
  const firstName = String(targetName).split(" ")[0] || (gift ? "there" : "there");
  const lines = [
    `Hi ${firstName}, this is RideXelite dispatch about reservation ${booking.reservation_ref || booking.id}.`,
    "",
    `Service: ${booking.service_name}`,
    booking.scheduled_at ? `Scheduled: ${new Date(booking.scheduled_at).toLocaleString()}` : "",
    booking.address ? `Address: ${booking.address}` : "",
    gift && !booking.gift_recipient_phone ? `(Gift booked by ${booking.customer_name || 'the sender'})` : "",
  ].filter(Boolean).join("\n");
  const href = `https://wa.me/${digits}?text=${encodeURIComponent(lines)}`;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`${testIdPrefix}-${booking.id}`}
      data-sound="coin"
      title={`Message ${targetName} on WhatsApp`}
      aria-label={`Open WhatsApp thread with ${targetName}`}
      className="inline-flex items-center justify-center w-8 h-8 rounded-full text-[#25D366] hover:bg-[#25D366]/15 transition"
    >
      <WhatsappLogo weight="fill" size={18} />
    </a>
  );
};

const AdminNav = () => {
  const items = [
    { to: "/admin", label: "Overview", icon: ChartLineUp, end: true },
    { to: "/admin/providers", label: "Providers", icon: Briefcase },
    { to: "/admin/bookings", label: "Bookings", icon: CalendarBlank },
    { to: "/admin/users", label: "Users", icon: Users },
  ];
  return (
    <aside className="w-full lg:w-64 shrink-0" data-testid="admin-nav">
      <div className="rounded-2xl border border-white/10 bg-[#121214] p-4">
        <div className="flex items-center gap-2 mb-6 px-2">
          <ShieldCheck size={18} weight="duotone" className="text-[#D4AF37]" />
          <div className="eyebrow gold-text">Admin</div>
        </div>
        <nav className="space-y-1">
          {items.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`admin-nav-${label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition ${
                  isActive
                    ? "bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30"
                    : "text-zinc-400 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <Icon weight="duotone" size={16} /> {label}
            </NavLink>
          ))}
        </nav>
      </div>
    </aside>
  );
};

const RegionFlag = ({ region }) => (
  <span>{region === "liberia" ? "🇱🇷" : "🇺🇸"}</span>
);

const Overview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/stats").then(r => setStats(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading || !stats) return <div className="text-zinc-400">Loading…</div>;

  const kpis = [
    { label: "Total users", value: stats.users.total, sub: `${stats.users.customers} customers · ${stats.users.providers} providers`, Icon: Users },
    { label: "Total bookings", value: stats.bookings.total, sub: `${stats.bookings.paid} paid · ${stats.bookings.reserved} reserved · ${stats.bookings.completed} completed`, Icon: CalendarBlank },
    { label: "Gross revenue", value: `$${stats.revenue.gross.toFixed(2)}`, sub: `Platform ${stats.revenue.platform_fees.toFixed(2)} · Payouts ${stats.revenue.provider_payouts.toFixed(2)}`, Icon: Money, gold: true },
    { label: "Providers pending", value: stats.providers.pending, sub: `${stats.providers.approved} approved`, Icon: Briefcase },
  ];

  return (
    <div data-testid="admin-overview">
      <div className="eyebrow mb-3">Platform overview</div>
      <h1 className="font-serif-display text-4xl text-white">Command center.</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mt-10">
        {kpis.map(k => (
          <div
            key={k.label}
            className={`rounded-2xl border p-6 ${k.gold ? "border-[#D4AF37]/40 bg-gradient-to-br from-[#121214] to-[#1a1815]" : "border-white/10 bg-[#121214]"}`}
          >
            <k.Icon weight="duotone" size={22} className="text-[#D4AF37]" />
            <div className="font-serif-display text-4xl text-white mt-3">{k.value}</div>
            <div className="eyebrow mt-2">{k.label}</div>
            <div className="text-xs text-zinc-500 mt-2 leading-relaxed">{k.sub}</div>
          </div>
        ))}
      </div>

      <div className="mt-12">
        <div className="eyebrow mb-6">Revenue by region</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(stats.revenue.by_region).map(([r, d]) => (
            <div key={r} className="rounded-2xl border border-white/10 bg-[#121214] p-6" data-testid={`region-revenue-${r}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <RegionFlag region={r} />
                  <div className="font-serif-display text-xl text-white capitalize">{r === "usa" ? "United States" : r}</div>
                </div>
                <div className="text-xs text-zinc-500">{d.count} bookings</div>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3">
                <div><div className="text-xs text-zinc-500">Gross</div><div className="font-serif-display text-lg text-white">${d.gross.toFixed(2)}</div></div>
                <div><div className="text-xs text-zinc-500">Platform</div><div className="font-serif-display text-lg text-[#D4AF37]">${d.platform_fees.toFixed(2)}</div></div>
                <div><div className="text-xs text-zinc-500">Payouts</div><div className="font-serif-display text-lg text-white">${d.provider_payouts.toFixed(2)}</div></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Providers = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = filter ? `?status_filter=${filter}` : "";
      const r = await api.get(`/admin/providers${params}`);
      setItems(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter]);

  const act = async (id, action) => {
    try {
      await api.post(`/admin/providers/${id}/${action}`);
      toast.success(`Provider ${action}d`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="admin-providers">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="eyebrow mb-3">Providers</div>
          <h1 className="font-serif-display text-4xl text-white">Applications & roster</h1>
        </div>
        <div className="inline-flex gap-1 rounded-full border border-white/10 bg-black/40 p-1 text-sm">
          {[
            { k: "", label: "All" },
            { k: "pending_review", label: "Pending" },
            { k: "approved", label: "Approved" },
            { k: "rejected", label: "Rejected" },
          ].map(f => (
            <button
              key={f.k}
              onClick={() => setFilter(f.k)}
              data-testid={`filter-${f.k || "all"}`}
              className={`px-4 py-1.5 rounded-full transition ${filter === f.k ? "bg-[#D4AF37] text-black" : "text-zinc-300 hover:text-white"}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? <div className="text-zinc-400">Loading…</div> : items.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-[#121214] p-10 text-center text-zinc-500">No providers.</div>
      ) : (
        <div className="space-y-3" data-testid="providers-list">
          {items.map(p => (
            <div key={p.id} className="rounded-2xl border border-white/10 bg-[#121214] p-6 flex flex-wrap items-center gap-6">
              <div className="flex-1 min-w-[220px]">
                <div className="font-serif-display text-lg text-white">{p.user?.full_name || "Unknown"}</div>
                <div className="text-xs text-zinc-500">{p.user?.email} · {p.user?.phone || "—"}</div>
              </div>
              <div className="text-sm text-zinc-400 flex flex-wrap gap-1">
                {p.verticals.map(v => (
                  <span key={v} className="px-2 py-1 rounded-full text-xs" style={{ background: `${VERTICAL_MAP[v]?.color}30`, border: `1px solid ${VERTICAL_MAP[v]?.color}` }}>
                    {VERTICAL_MAP[v]?.name}
                  </span>
                ))}
              </div>
              <div className="text-sm text-zinc-400 flex items-center gap-2 max-w-xs">
                <MapPin size={14} /> {p.service_area}
              </div>
              <div className="text-sm text-zinc-400">{p.experience_years}y exp</div>
              <div className={`px-3 py-1 rounded-full text-xs ${
                p.status === "approved" ? "bg-emerald-500/10 text-emerald-400" :
                p.status === "rejected" ? "bg-red-500/10 text-red-400" :
                "bg-yellow-500/10 text-yellow-400"
              }`}>{p.status}</div>
              {p.status === "pending_review" && (
                <div className="flex gap-2">
                  <button
                    onClick={() => act(p.id, "approve")}
                    data-testid={`approve-${p.id}`}
                    className="px-4 py-2 rounded-full bg-[#D4AF37] text-black text-sm font-semibold flex items-center gap-1"
                  >
                    <CheckCircle size={14} weight="fill" /> Approve
                  </button>
                  <button
                    onClick={() => act(p.id, "reject")}
                    data-testid={`reject-${p.id}`}
                    className="px-4 py-2 rounded-full border border-white/20 text-sm text-zinc-300 hover:text-red-400 flex items-center gap-1"
                  >
                    <XCircle size={14} /> Reject
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Bookings = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [region, setRegion] = useState("");
  const [status, setStatus] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (region) q.set("region", region);
      if (status) q.set("status_filter", status);
      const r = await api.get(`/admin/bookings?${q.toString()}`);
      setItems(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [region, status]);

  const cancel = async (id) => {
    if (!window.confirm("Cancel this booking?")) return;
    try {
      await api.post(`/admin/bookings/${id}/cancel`);
      toast.success("Booking cancelled");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const confirmMomo = async (id) => {
    if (!window.confirm("Confirm Mobile Money received for this booking?")) return;
    try {
      await api.post(`/admin/bookings/${id}/confirm-momo`);
      toast.success("Mobile Money confirmed · booking marked paid");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="admin-bookings">
      <div className="eyebrow mb-3">Bookings</div>
      <h1 className="font-serif-display text-4xl text-white">All bookings</h1>

      <div className="mt-8 flex flex-wrap gap-3">
        <select
          value={region}
          onChange={(e) => setRegion(e.target.value)}
          data-testid="filter-region"
          className="px-4 py-2 rounded-full bg-[#121214] border border-white/10 text-sm text-white"
        >
          <option value="">All regions</option>
          <option value="usa">🇺🇸 USA</option>
          <option value="liberia">🇱🇷 Liberia</option>
        </select>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          data-testid="filter-status"
          className="px-4 py-2 rounded-full bg-[#121214] border border-white/10 text-sm text-white"
        >
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="reserved">Reserved</option>
          <option value="paid">Paid</option>
          <option value="assigned">Assigned</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <div className="text-sm text-zinc-500 self-center ml-auto">{items.length} results</div>
      </div>

      {loading ? <div className="mt-8 text-zinc-400">Loading…</div> : (
        <div className="mt-6 rounded-2xl border border-white/10 bg-[#121214] overflow-hidden" data-testid="bookings-table">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-zinc-500 border-b border-white/10">
              <tr>
                <th className="text-left px-6 py-4">Ref</th>
                <th className="text-left px-6 py-4">Region</th>
                <th className="text-left px-6 py-4">Service</th>
                <th className="text-left px-6 py-4">Scheduled</th>
                <th className="text-right px-6 py-4">Amount</th>
                <th className="text-right px-6 py-4">Fee / Payout</th>
                <th className="text-left px-6 py-4">Status</th>
                <th className="px-6 py-4"></th>
              </tr>
            </thead>
            <tbody>
              {items.map(b => (
                <tr key={b.id} className="border-b border-white/5 hover:bg-white/[0.02] transition">
                  <td className="px-6 py-4 font-mono text-xs text-[#D4AF37]">{b.reservation_ref || b.id.slice(0,8)}</td>
                  <td className="px-6 py-4"><RegionFlag region={b.region} /> {b.region_label || b.region}</td>
                  <td className="px-6 py-4 text-white">{b.service_name}</td>
                  <td className="px-6 py-4 text-zinc-400 text-xs">{new Date(b.scheduled_at).toLocaleString()}</td>
                  <td className="px-6 py-4 text-right font-medium text-white">${b.amount.toFixed(2)}</td>
                  <td className="px-6 py-4 text-right text-xs text-zinc-400">${b.platform_fee.toFixed(2)} / ${b.provider_payout.toFixed(2)}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-[10px] uppercase ${
                      b.status === "completed" ? "bg-[#D4AF37]/10 text-[#D4AF37]" :
                      b.status === "assigned" ? "bg-blue-500/10 text-blue-400" :
                      b.status === "paid" ? "bg-emerald-500/10 text-emerald-400" :
                      b.status === "reserved" ? "bg-cyan-500/10 text-cyan-400" :
                      b.status === "cancelled" ? "bg-red-500/10 text-red-400" :
                      "bg-yellow-500/10 text-yellow-400"
                    }`}>{b.status}</span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-3">
                      <BookingWhatsAppLink booking={b} testIdPrefix="admin-wa" />
                      {b.payment_mode === "mobile_money" && b.payment_status !== "paid" && b.status !== "cancelled" && (
                        <button
                          onClick={() => confirmMomo(b.id)}
                          data-testid={`confirm-momo-${b.id}`}
                          data-sound="coin"
                          className="text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition"
                        >
                          Confirm MoMo
                        </button>
                      )}
                      {b.status !== "cancelled" && b.status !== "completed" && (
                        <button
                          onClick={() => cancel(b.id)}
                          data-testid={`cancel-${b.id}`}
                          className="text-xs text-zinc-500 hover:text-red-400 transition"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={8} className="px-6 py-16 text-center text-zinc-500">No bookings match your filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const Users2 = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState("");

  useEffect(() => {
    setLoading(true);
    const q = role ? `?role=${role}` : "";
    api.get(`/admin/users${q}`).then(r => setItems(r.data)).finally(() => setLoading(false));
  }, [role]);

  return (
    <div data-testid="admin-users">
      <div className="eyebrow mb-3">Users</div>
      <h1 className="font-serif-display text-4xl text-white">Directory</h1>

      <div className="mt-8 inline-flex gap-1 rounded-full border border-white/10 bg-black/40 p-1 text-sm">
        {[
          { k: "", label: "All" },
          { k: "customer", label: "Customers" },
          { k: "provider", label: "Providers" },
          { k: "admin", label: "Admins" },
        ].map(f => (
          <button
            key={f.k}
            onClick={() => setRole(f.k)}
            data-testid={`role-filter-${f.k || "all"}`}
            className={`px-4 py-1.5 rounded-full transition ${role === f.k ? "bg-[#D4AF37] text-black" : "text-zinc-300 hover:text-white"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? <div className="mt-8 text-zinc-400">Loading…</div> : (
        <div className="mt-6 rounded-2xl border border-white/10 bg-[#121214] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-zinc-500 border-b border-white/10">
              <tr>
                <th className="text-left px-6 py-4">Name</th>
                <th className="text-left px-6 py-4">Email</th>
                <th className="text-left px-6 py-4">Phone</th>
                <th className="text-left px-6 py-4">Role</th>
                <th className="text-left px-6 py-4">Joined</th>
              </tr>
            </thead>
            <tbody>
              {items.map(u => (
                <tr key={u.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="px-6 py-4 text-white">{u.full_name}</td>
                  <td className="px-6 py-4 text-zinc-400">{u.email}</td>
                  <td className="px-6 py-4 text-zinc-400">{u.phone || "—"}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-[10px] uppercase ${
                      u.role === "admin" ? "bg-[#D4AF37]/10 text-[#D4AF37]" :
                      u.role === "provider" ? "bg-blue-500/10 text-blue-400" :
                      "bg-zinc-500/10 text-zinc-300"
                    }`}>{u.role}</span>
                  </td>
                  <td className="px-6 py-4 text-zinc-500 text-xs">{new Date(u.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={5} className="px-6 py-16 text-center text-zinc-500">No users.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default function AdminDashboard() {
  const user = getUser();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") {
    return (
      <div className="max-w-3xl mx-auto p-16 text-center">
        <ShieldCheck size={48} weight="duotone" className="text-red-400 mx-auto" />
        <h1 className="mt-4 font-serif-display text-3xl text-white">Admin access required.</h1>
        <p className="mt-4 text-zinc-400">Your account does not have admin privileges.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <div className="flex flex-col lg:flex-row gap-8">
        <AdminNav />
        <div className="flex-1 min-w-0">
          <Routes>
            <Route index element={<Overview />} />
            <Route path="providers" element={<Providers />} />
            <Route path="bookings" element={<Bookings />} />
            <Route path="users" element={<Users2 />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}
